import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createOperatorApi, type OperatorApi } from '../api/client';

const STORAGE_KEY = 'aexgora.stoa.operator';

interface StoredSession {
  sessionToken: string;
  operatorId: string;
}

function loadSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (parsed.sessionToken && parsed.operatorId) {
      return { sessionToken: parsed.sessionToken, operatorId: parsed.operatorId };
    }
    return null;
  } catch {
    return null;
  }
}

function saveSession(s: StoredSession) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

function clearStoredSession() {
  localStorage.removeItem(STORAGE_KEY);
}

interface OperatorAuthValue {
  sessionToken: string | null;
  operatorId: string | null;
  isAuthenticated: boolean;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  api: OperatorApi;
}

const OperatorAuthContext = createContext<OperatorAuthValue | null>(null);

export const OperatorAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initial = loadSession();
  const [sessionToken, setSessionToken] = useState<string | null>(initial?.sessionToken ?? null);
  const [operatorId, setOperatorId] = useState<string | null>(initial?.operatorId ?? null);

  const tokenRef = useRef<string | null>(sessionToken);
  tokenRef.current = sessionToken;

  const clearLocal = useCallback(() => {
    clearStoredSession();
    setSessionToken(null);
    setOperatorId(null);
    tokenRef.current = null;
  }, []);

  const api = useMemo(
    () =>
      createOperatorApi({
        getToken: () => tokenRef.current,
        onUnauthorized: () => clearLocal(),
      }),
    [clearLocal],
  );

  const acceptSession = useCallback((token: string, id: string) => {
    saveSession({ sessionToken: token, operatorId: id });
    setSessionToken(token);
    setOperatorId(id);
    tokenRef.current = token;
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await api.loginOperator(email, password);
      acceptSession(res.session_token, res.operator.operator_id);
    },
    [api, acceptSession],
  );

  const register = useCallback(
    async (email: string, password: string, displayName: string) => {
      const res = await api.registerOperator(email, password, displayName);
      if (res.session_token) {
        acceptSession(res.session_token, res.operator_id);
        return;
      }
      await login(email, password);
    },
    [api, acceptSession, login],
  );

  const logout = useCallback(async () => {
    if (tokenRef.current) {
      try {
        await api.logoutOperator();
      } catch {
        // Logout best-effort; we always clear local state.
      }
    }
    clearLocal();
  }, [api, clearLocal]);

  const value = useMemo<OperatorAuthValue>(
    () => ({
      sessionToken,
      operatorId,
      isAuthenticated: sessionToken !== null,
      register,
      login,
      logout,
      api,
    }),
    [sessionToken, operatorId, register, login, logout, api],
  );

  return <OperatorAuthContext.Provider value={value}>{children}</OperatorAuthContext.Provider>;
};

export function useOperatorAuth(): OperatorAuthValue {
  const ctx = useContext(OperatorAuthContext);
  if (!ctx) throw new Error('useOperatorAuth must be used inside OperatorAuthProvider');
  return ctx;
}
